import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilePlus2, FileText } from 'lucide-react';
import { SectionHeader } from '../ui/SectionHeader';
import { PartnerReportForm } from './PartnerReportForm';
import { PartnerReportList } from './PartnerReportList';
import type { PartnerDetail, PartnerUpdatesStatusFilter } from '../../types/partners';
import type { PartnerNotice } from '../../utils/partnerSync';

interface PartnerUpdatesSectionProps {
  detail: PartnerDetail;
  canEdit: boolean;
  /** The viewer can open Modpack Configs, for "Update the cluster". */
  canOpenConfigs: boolean;
  onChanged: () => void | Promise<void>;
  onNotice: (notice: PartnerNotice) => void;
}

const FILTERS: { id: PartnerUpdatesStatusFilter; label: string }[] = [
  { id: 'open', label: 'Waiting on staff' },
  { id: 'all', label: 'All reports' },
];

/**
 * The partner's update reports, open ones first: what they told us, where
 * each one stands, and a form for logging one on their behalf. `?report=`
 * opens one report, whatever the filter.
 */
export const PartnerUpdatesSection = ({ detail, canEdit, canOpenConfigs, onChanged, onNotice }: PartnerUpdatesSectionProps) => {
  const [params, setParams] = useSearchParams();
  const focusId = params.get('report');
  const [filter, setFilter] = useState<PartnerUpdatesStatusFilter>(detail.open_updates > 0 ? 'open' : 'all');
  const [logging, setLogging] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const setFocus = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('report', id);
        else next.delete('report');
        return next;
      },
      { replace: true },
    );

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Update reports"
        icon={FileText}
        count={detail.open_updates}
        actions={
          canEdit &&
          !logging && (
            <button type="button" className="btn-primary" onClick={() => setLogging(true)}>
              <FilePlus2 className="w-4 h-4" />
              Log an update
            </button>
          )
        }
      />
      <p className="text-sm text-glass-400">
        Partners report new versions from their channel, often before CurseForge lists them. Versions Argus finds on CurseForge first show here too,
        marked as not reported. Move each one on as it heads for our servers; the partner sees the status on its card.
      </p>

      {logging && (
        <PartnerReportForm
          creatorId={detail.creator.id}
          packs={detail.packs.map((p) => ({ modpack_id: p.modpack_id, name: p.name }))}
          managed={detail.discord.managed}
          onNotice={onNotice}
          onCancel={() => setLogging(false)}
          onCreated={(report) => {
            setLogging(false);
            setFilter('open');
            setFocus(report.id);
            setRefreshKey((k) => k + 1);
            void onChanged();
          }}
        />
      )}

      <div className="segmented max-w-full" role="tablist" aria-label="Which reports">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className="segmented-item" data-active={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      <PartnerReportList
        query={{ creator_id: detail.creator.id, status: filter }}
        canEdit={canEdit}
        focusId={focusId}
        onFocusChange={setFocus}
        packs={detail.packs}
        canOpenConfigs={canOpenConfigs}
        onNotice={onNotice}
        refreshKey={refreshKey}
        onChanged={() => void onChanged()}
        emptyTitle={filter === 'open' ? 'Nothing is waiting on staff' : 'No update reports yet'}
        emptyDescription={
          filter === 'open'
            ? 'Every report from this partner has been dealt with.'
            : 'When the partner reports a new version from their channel, or staff log one here, it shows up in this list.'
        }
      />
    </div>
  );
};
